import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router";
import * as Sentry from "@sentry/react";

import "./index.css";
import "./i18n";

// Core components - eagerly loaded as they're used across all routes
import Header from "./components/organisms/common/Header";
import Footer from "./components/organisms/common/Footer";
import TabBar from "./components/organisms/common/TabBar";
import PushHandler from "./components/common/PushHandler";
import RefreshUser from "./components/common/RefreshUser";
import Loading from "./components/atoms/Loading";
import RegisterNow from "./components/molecules/common/RegisterNow";
import DeepLinkHandler from "./components/common/DeepLinkHandler";
import SafeAreaView from "./components/molecules/common/SafeArea";
import ThemeHandler from "./components/common/ThemeHandler";
import ChunkErrorBoundary from "./components/common/ChunkErrorBoundary";

// Auth routes
const LoginScreen = lazy(() => import("./pages/auth/Login"));
const LoginSSOScreen = lazy(() => import("./pages/auth/LoginSSO"));
const Register = lazy(() => import("./pages/auth/Register"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));

// Gallery routes
const Gallery = lazy(() => import("./pages/gallery/Gallery"));
const GalleryHallOfFame = lazy(() =>
  import("./pages/gallery/GalleryHallOfFame")
);
const GalleryMyPosts = lazy(() => import("./pages/gallery/GalleryMyPosts"));
const GalleryPost = lazy(() => import("./pages/gallery/GalleryPost"));

// Exchange routes
const Exchange = lazy(() => import("./pages/exchange/Exchange"));
const Exchanges = lazy(() => import("./pages/exchanges/Exchanges"));
const ExchangesCalendar = lazy(() =>
  import("./pages/exchanges/ExchangesCalendar")
);
const CreateExchange = lazy(() => import("./pages/exchange/CreateExchange"));
const ManageExchange = lazy(() => import("./pages/exchange/ManageExchange"));
const MyRematches = lazy(() => import("./pages/exchanges/MyRematches"));
const RequiredRematchers = lazy(() =>
  import("./pages/exchanges/RequiredRematchers")
);
const MyExchanges = lazy(() => import("./pages/exchanges/MyExchanges"));
const DirectRematchers = lazy(() =>
  import("./pages/exchanges/DirectRematchers")
);

// Blog routes
const Blog = lazy(() => import("./pages/discover/Blog"));
const BlogPost = lazy(() => import("./pages/discover/BlogPost"));

// Discover routes
const HowItWorks = lazy(() => import("./pages/discover/HowItWorks"));
const WhoWeAre = lazy(() => import("./pages/discover/WhoWeAre"));
const ContactAndSupport = lazy(() =>
  import("./pages/discover/ContactAndSupport")
);
const SupportUs = lazy(() => import("./pages/discover/SupportUs"));

// Other pages
const Home = lazy(() => import("./pages/Home"));
const MyAccount = lazy(() => import("./pages/account/MyAccount"));
const AccountOnboarding = lazy(() =>
  import("./pages/account/AccountOnboarding")
);
const Inbox = lazy(() => import("./pages/Inbox"));
const Merchandise = lazy(() => import("./pages/Merchandise"));
const MyPremium = lazy(() => import("./pages/my-premium/MyPremium"));
const Premium = lazy(() => import("./pages/Premium"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Profile = lazy(() => import("./pages/Profile"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

// Admin routes
const ManageDnm = lazy(() => import("./pages/admin/ManageDnm"));
const ManageFaq = lazy(() => import("./pages/admin/ManageFaq"));
const CreateBlog = lazy(() => import("./pages/admin/CreateBlog"));
const ManageProof = lazy(() => import("./pages/admin/ManageProof"));
const ManageUser = lazy(() => import("./pages/admin/ManageUser"));
const ManageUsers = lazy(() => import("./pages/admin/ManageUsers"));
const UserProof = lazy(() => import("./pages/admin/UserProof"));

Sentry.init({
  dsn: "https://149ad3068c8ddca83292e6bee13afe88@o4509109017247744.ingest.de.sentry.io/4509109018886225",
});

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <BrowserRouter>
      <SafeAreaView>
        <ThemeHandler />
        <Header />
        <Suspense fallback={<Loading />}>
          <ChunkErrorBoundary>
            <Routes>
              <Route path="/" element={<Home />} />

              {/* Auth routes */}
              <Route path="/login" element={<LoginScreen />} />
              <Route path="/login/sso" element={<LoginSSOScreen />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/forgot-password/reset"
                element={<ResetPassword />}
              />

              {/* Account routes */}
              <Route path="/account" element={<MyAccount />} />
              <Route
                path="/account/onboarding"
                element={<AccountOnboarding />}
              />

              {/* Premium routes */}
              <Route path="/account/premium" element={<MyPremium />} />
              <Route path="/premium" element={<Premium />} />

              {/* Blog routes */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:titleSafe" element={<BlogPost />} />

              {/* Discover routes */}
              <Route
                path="/discover/contact-us"
                element={<ContactAndSupport />}
              />
              <Route path="/discover/how-it-works" element={<HowItWorks />} />
              <Route path="/discover/support-us" element={<SupportUs />} />
              <Route path="/discover/who-we-are" element={<WhoWeAre />} />

              {/* Exchange routes */}
              <Route path="/exchange/create" element={<CreateExchange />} />
              <Route
                path="/exchange/manage/:titleSafe"
                element={<ManageExchange />}
              />
              <Route path="/exchange/:titleSafe" element={<Exchange />} />
              <Route path="/exchanges/my-exchanges" element={<MyExchanges />} />
              <Route path="/exchanges/my-rematches" element={<MyRematches />} />
              <Route
                path="/exchanges/required"
                element={<RequiredRematchers />}
              />
              <Route
                path="/exchanges/required/:titleSafe/:countryCode"
                element={<DirectRematchers />}
              />
              <Route
                path="/exchanges/upcoming"
                element={<ExchangesCalendar />}
              />
              <Route path="/exchanges/:exchangeType" element={<Exchanges />} />

              {/* Gallery routes */}
              <Route path="/gallery" element={<Gallery />} />
              <Route
                path="/gallery/:exchangeSafe/:titleSafe"
                element={<GalleryPost />}
              />
              <Route
                path="/gallery/hall-of-fame"
                element={<GalleryHallOfFame />}
              />
              <Route path="/gallery/my-posts" element={<GalleryMyPosts />} />

              {/* Admin routes */}
              <Route path="/admin/dnm" element={<ManageDnm />} />
              <Route path="/admin/faq" element={<ManageFaq />} />
              <Route path="/admin/blog" element={<CreateBlog />} />
              <Route path="/admin/proof" element={<ManageProof />} />
              <Route
                path="/admin/proof/:exchangeId/:userId"
                element={<UserProof />}
              />
              <Route
                path="/admin/proof/:exchangeId/:userId/:type/:gifteeId"
                element={<UserProof />}
              />
              <Route path="/admin/user/:userId" element={<ManageUser />} />
              <Route path="/admin/users" element={<ManageUsers />} />

              {/* Other routes */}
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/merchandise" element={<Merchandise />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/profile/:nickname" element={<Profile />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
            </Routes>
          </ChunkErrorBoundary>
        </Suspense>
        <RegisterNow />
        <Footer />
        <TabBar />
        <PushHandler />
        <RefreshUser />
        <DeepLinkHandler />
      </SafeAreaView>
    </BrowserRouter>
  </StrictMode>
);
